import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { Lock } from "lucide-react";
import Tilt3D from "./Tilt3D";
import HeartButton from "./HeartButton";
import { useLightMode } from "../lib/useDevice";

export default function ProductCard({ product, index = 0, dark = false }) {
  const { add, setOpen } = useCart();
  const { format } = useCurrency();
  const { user } = useAuth();
  const light = useLightMode();
  const locked = product.prive_only && !user?.prive;
  const hasOptions = (product.variants?.length || 0) > 0 || (product.sizes?.length || 0) > 0;

  const handleAdd = () => {
    const v = product.variants?.[0];
    add(product, v?.label || product.sizes?.[0] || null, v?.price ?? product.price);
    setOpen(true);
    toast.success(`${product.name} added to your cart`, {
      action: { label: "View", onClick: () => setOpen(true) },
    });
  };

  return (
    <motion.div
      data-testid={`product-card-${product.id}`}
      initial={light ? { opacity: 0, y: 28 } : { opacity: 0, y: 70, rotateX: 18 }}
      whileInView={light ? { opacity: 1, y: 0 } : { opacity: 1, y: 0, rotateX: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: light ? 0.5 : 0.9, delay: (index % 4) * (light ? 0.05 : 0.1), ease: [0.22, 1, 0.36, 1] }}
      style={light ? undefined : { transformPerspective: 1200, transformOrigin: "top center" }}
    >
      <div className="group/card relative">
        <Tilt3D max={12} light={light} className="aspect-[3/4] overflow-hidden bg-sand">
          <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`} data-cursor="wishlist" className="block h-full w-full">
            <img
              src={product.image}
              alt={product.name}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.08]"
            />
            {product.gallery?.[1] && product.gallery[1] !== product.image && (
              <img
                data-testid={`product-look-${product.id}`}
                src={product.gallery[1]}
                alt={`${product.name} worn`}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700"
              />
            )}
          </Link>
          <div className="absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent opacity-80 pointer-events-none" />
          <HeartButton id={product.id} name={product.name} style={{ transform: "translateZ(50px)" }} className="absolute top-3 right-3 z-10" />
          {product.tag && (
            <span
              style={{ transform: "translateZ(50px)" }}
              className="absolute top-4 left-4 text-[9px] tracking-[0.3em] uppercase bg-ivory/90 backdrop-blur-md px-3 py-1.5 text-ink pointer-events-none"
            >
              {product.tag}
            </span>
          )}
          {locked && (
            <div data-testid={`prive-lock-${product.id}`} style={{ transform: "translateZ(60px)" }} className="absolute inset-x-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-2 text-ivory pointer-events-none">
              <span className="w-12 h-12 rounded-full bg-ink/70 backdrop-blur border border-gold/60 flex items-center justify-center"><Lock size={16} className="text-gold-light" /></span>
              <span className="text-[9px] tracking-[0.35em] uppercase bg-ink/70 px-3 py-1.5">Privé members only</span>
            </div>
          )}
          <img
            src="/brand/logo-seal.png"
            alt=""
            aria-hidden="true"
            style={{ transform: "translateZ(40px)" }}
            className="absolute top-4 right-4 w-8 h-8 opacity-90 pointer-events-none drop-shadow"
          />
          <span
            style={{ transform: "translateZ(40px)" }}
            className="absolute bottom-4 right-4 font-serif text-2xl text-ivory drop-shadow-md pointer-events-none"
          >
            {format(product.price)}
          </span>
        </Tilt3D>
        {!locked && (
        <button
          data-testid={`add-to-cart-${product.id}`}
          onClick={handleAdd}
          className="absolute bottom-[4.9rem] md:bottom-[5.1rem] left-4 right-4 z-10 translate-y-16 opacity-0 group-hover/card:translate-y-0 group-hover/card:opacity-100 transition-[transform,opacity] duration-500 ease-out bg-ivory text-ink text-[10px] tracking-[0.25em] uppercase font-medium py-3.5 hover:bg-gold hover:text-ink shadow-lg"
        >
          {hasOptions ? "Quick add" : "Add to Cart"}
        </button>
        )}
        <div className="pt-5 flex items-start justify-between gap-4">
          <div>
            <p className={`text-[9px] tracking-[0.35em] uppercase ${dark ? "text-ivory/45" : "text-ink/45"}`}>{product.category}</p>
            <Link to={`/product/${product.id}`} className="block">
              <h3 className={`font-serif text-lg md:text-xl mt-1.5 leading-snug transition-colors ${dark ? "text-ivory hover:text-gold-light" : "hover:text-gold-dark"}`}>{product.name}</h3>
            </Link>
          </div>
          <p className={`font-light text-sm md:text-base pt-4 whitespace-nowrap ${dark ? "text-gold-light" : "text-gold-dark"}`}>{format(product.price)}</p>
        </div>
      </div>
    </motion.div>
  );
}
